import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Badge,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Avatar,
  Drawer,
  ActionIcon,
  Box,
  Tooltip,
  Skeleton,
} from '@mantine/core';
import {
  IconTable,
  IconLayoutGrid,
  IconSearch,
  IconEye,
  IconAdjustments,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { Flex, Select, TextInput, CCMSDataTable, StreamProgress, ListPagination } from '@/components';
import { ContactCard, type Contact } from '../Components/ContactCard';
import { EmptyState } from '../Components/EmptyState';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import {
  LeaderModelView,
  useLeaderStreaming,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { resolveAvatarSrc, getInitialsFromName, reportAvatarLoadError } from '@/utils/avatar';
import { useAssignedLeaders, subtractListed, splitName } from '../unlistedAssignees';
import { sortRows } from '@/utils/tableSort';

/**
 * A leader row, either streamed from `leaderService.list()` or synthesised for someone who owns a case
 * but has no leader record. The `unlisted` marker is the only thing that separates the two.
 */
type LeaderRow = LeaderModelView & {
  /** Present only on synthesised rows. Carries how many cases reference the person. */
  unlisted?: { caseCount: number };
};

// ─── Page component ────────────────────────────────────────────────────────────
export default function CareSupportLeadersPage() {
  const navigate = useNavigate();
  const { refetch } = useSidebarCountsContext();

  // ─── View / pagination / sort ──────────────────────────────────────────────
  const [view, setView]             = useState<'table' | 'cards'>('cards');
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize]     = useState(10);
  const [cardSize, setCardSize]     = useState(12); // 12 cards per page in card view
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // ─── Add Leader is now a dedicated page (see AddLeaderWizard.tsx),
  // navigated to below rather than opened in a modal/drawer ───────────────

  // ─── Search / filters ──────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]                 = useState('');
  const [filtersOpen, setFiltersOpen]               = useState(false);
  const [selectedRole, setSelectedRole]             = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation]     = useState<string | null>(null);

  const isFilterActive = Boolean(
    selectedRole || selectedLocation || searchTerm
  );

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedRole(null);
    setSelectedLocation(null);
  };

  // ─── Leader display helpers ────────────────────────────────────────────────
  const getLeaderFullName = (leader: LeaderRow) => {
    const fromParts = [leader.firstName, leader.surname].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    const full = leader.fullName?.trim();
    if (full) return full;
    // Reject displayName if it looks like a raw userId GUID
    const display = leader.displayName?.trim();
    if (display && display !== leader.userId) return display;
    return 'Unknown';
  };

  const getLeaderAvatar  = (leader: LeaderRow) => resolveAvatarSrc(leader.avatar);
  const getLeaderStatus  = (leader: LeaderRow) => leader.isActive ?? false;
  const getLeaderContact = (leader: LeaderRow) => leader.email || leader.phone || 'N/A';

  // Maps a LeaderModelView into the generic Contact shape ContactCard expects.
  const leaderToContact = (leader: LeaderRow): Contact => {
    const [firstName, ...rest] = getLeaderFullName(leader).split(' ');
    return {
      id: leader.userId || String(leader.memberID),
      firstName: leader.firstName || firstName || '',
      lastName: leader.surname || rest.join(' ') || '',
      avatar: getLeaderAvatar(leader) ?? null,
      typeName: leader.typeName ?? undefined,
      typeColor: leader.typeColor ?? undefined,
      activeCaseCount: leader.activeCaseCount ?? 0,
      needCount: leader.activeCaseCount ?? 0,
      isActive: getLeaderStatus(leader),
      unlistedNote: leader.unlisted ? 'No user account' : undefined,
    };
  };

  // ─── Data streaming ────────────────────────────────────────────────────────
  // Leaders arrive in 50-row batches via the SDK stream, so cards/rows paint progressively rather
  // than waiting for the whole list. Search and the type/location filters stay client-side —
  // the full set is in memory, so filtering locally gives the same result without re-streaming.
  const {
    items: leaders,
    isLoading: loading,
    progress,
    loadedCount,
    load: fetchAll,
  } = useLeaderStreaming(undefined, 50);

  // Kept in a ref so the sidebar-count refresh doesn't participate in the stream effect's deps.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    fetchAll().then(() => refetchRef.current());
  }, [fetchAll]);

  // ─── Leaders who own a case but have no leader record ──────────────────────
  // These never come back from `leaderService.list()`, so they are rebuilt from the case stream and
  // merged in below. See `../unlistedAssignees.ts` for why the case list is the source.
  const { people: assignedLeaders, isLoading: assignedLoading } = useAssignedLeaders();

  const unlistedRows = useMemo<LeaderRow[]>(() => {
    const listed = new Set(leaders.map((l) => l.userId).filter(Boolean));
    return subtractListed(assignedLeaders, listed).map((person) => {
      const { firstName, lastName } = splitName(person.name);
      return {
        userId: person.userId,
        memberID: 0,
        firstName,
        surname: lastName,
        avatar: person.avatar,
        typeID: 0,
        typeName: person.typeName,
        typeColor: person.typeColor,
        supportsVirtual: false,
        supportsInPerson: false,
        professional: false,
        activeCaseCount: person.assignmentCount,
        needsCount: 0,
        isActive: false,
        unlisted: { caseCount: person.assignmentCount },
      };
    });
  }, [assignedLeaders, leaders]);

  const allLeaders = useMemo<LeaderRow[]>(
    () => [...leaders, ...unlistedRows],
    [leaders, unlistedRows],
  );

  // ─── Frontend filtering ────────────────────────────────────────────────────
  const filteredLeaders = allLeaders.filter((leader) => {
    const fullName = getLeaderFullName(leader);
    const contact  = getLeaderContact(leader);

    const matchesSearch =
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (leader.typeName ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (leader.bio ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole     = selectedRole     ? leader.typeName === selectedRole : true;
    const matchesLocation = selectedLocation ? leader.locations?.some((loc) => loc.locationName === selectedLocation) : true;

    return matchesSearch && matchesRole && matchesLocation;
  });

  // ─── Frontend sorting ──────────────────────────────────────────────────────
  const columns: TableColumn<LeaderRow>[] = [
    {
      key: 'avatar',
      header: 'Avatar',
      accessor: (leader) => leader,
      sortable: false,
      width: 80,
      align: 'center',
      render: (_, leader) => {
        const name   = getLeaderFullName(leader);
        const avatar = getLeaderAvatar(leader);
        return (
          <Avatar
            src={avatar}
            alt={name}
            size="md"
            imageProps={{ onError: () => reportAvatarLoadError(avatar) }}
          >
            {getInitialsFromName(name)}
          </Avatar>
        );
      },
    },
    {
      key: 'name',
      header: 'Name',
      accessor: (leader) => getLeaderFullName(leader),
      sortable: true,
      render: (value, leader) => (
        <Stack gap={2}>
          <Group gap="xs" align="center">
            <Text fw={600}>{value}</Text>
            {leader.unlisted ? (
              <Badge color="orange" variant="light" size="xs">
                No user account
              </Badge>
            ) : (
              <Badge color={getLeaderStatus(leader) ? 'green' : 'red'} variant="light" size="xs">
                {getLeaderStatus(leader) ? 'Active' : 'Inactive'}
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            {leader.unlisted
              ? `Owns ${leader.unlisted.caseCount} case${leader.unlisted.caseCount === 1 ? '' : 's'}, not on the leader list`
              : leader.email ?? leader.phone ?? 'No contact'}
          </Text>
        </Stack>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      accessor: (leader) => leader.typeName ?? 'N/A',
      sortable: true,
      render: (value, leader) => (
        <Badge
          variant="light"
          style={{
            backgroundColor: leader.typeColor ? `${leader.typeColor}22` : undefined,
            color: leader.typeColor ?? undefined,
          }}
        >
          {value}
        </Badge>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      accessor: (leader) => leader.phone ?? 'N/A',
      sortable: true,
    },
    {
      key: 'activeCases',
      header: 'Active Cases',
      accessor: (leader) => leader.activeCaseCount ?? 0,
      sortable: true,
      align: 'center',
    },
    {
      key: 'needCount',
      header: 'Need Count',
      accessor: (leader) => leader.activeCaseCount ?? 0,
      sortable: true,
      align: 'center',
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (leader) => leader,
      sortable: false,
      align: 'center',
      render: (_, leader) => (
        <Group gap={6} justify="center">
          <Button
            variant="light"
            color="blue"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              goToLeader(leader);
            }}
          >
            <IconEye size={16} />
          </Button>
        </Group>
      ),
    },
  ];


  /**
   * Sorted by the clicked column's own `accessor`, so the key can never disagree with the cell.
   * ⚠️ Replaces a `switch` kept in parallel with the columns array: `phone` was marked sortable with no
   * case, so clicking Phone sorted every row by `''` and appeared to do nothing.
   */
  const sortedLeaders = useMemo(
    () => sortRows(filteredLeaders, columns, sortConfig),
    [filteredLeaders, columns, sortConfig],
  );

  // ─── Pagination ────────────────────────────────────────────────────────────
  const pageSizeForView = view === 'cards' ? cardSize : pageSize;
  const totalItems = sortedLeaders.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSizeForView));

  // Reset to page 1 whenever filters or page size change
  useEffect(() => { setActivePage(1); }, [
    searchTerm, selectedRole, selectedLocation, pageSize, cardSize, view,
  ]);

  // Clamp activePage if the total number of pages shrinks
  useEffect(() => {
    if (activePage > totalPages) setActivePage(totalPages);
  }, [activePage, totalPages]);

  const paginatedLeaders = useMemo(() => {
    const firstIndex = (activePage - 1) * pageSizeForView;
    return sortedLeaders.slice(firstIndex, firstIndex + pageSizeForView);
  }, [sortedLeaders, activePage, pageSizeForView]);

  // ─── Empty-state helpers ───────────────────────────────────────────────────
  const listLoading = loading || assignedLoading;
  const hasNoLeadersAtAll = !listLoading && allLeaders.length === 0;
  const hasNoFilteredResults = !listLoading && allLeaders.length > 0 && sortedLeaders.length === 0;

  /** How many rows on this page exist only because a case points at them. */
  const unlistedCount = unlistedRows.length;

  /**
   * Opens the detail page. For an unlisted person the cached name rides along in router state, because
   * the detail page cannot look it up: there is no leader record to fetch.
   */
  const goToLeader = (leader: LeaderRow) =>
    navigate(
      `/Information/CareSupportLeaders/Leaders/${leader.userId || leader.memberID}`,
      leader.unlisted ? { state: { unlistedName: getLeaderFullName(leader) } } : undefined,
    );

  // ─── Table columns ─────────────────────────────────────────────────────────
  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      <Title order={2}>Care Support Leaders</Title>
      <Text mb={unlistedCount > 0 ? 'xs' : 'lg'}>
        Manage who serves as a care support leader across the church.
      </Text>
      {unlistedCount > 0 && (
        <Text size="sm" c="orange" mb="lg">
          {unlistedCount} {unlistedCount === 1 ? 'person owns' : 'people own'} a case but{' '}
          {unlistedCount === 1 ? 'has' : 'have'} no user account, so{' '}
          {unlistedCount === 1 ? 'it is' : 'they are'} not on the leader list. Open{' '}
          {unlistedCount === 1 ? 'the entry' : 'an entry'} to create the account.
        </Text>
      )}

      {/* ── Search and Filter bar ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by name, role, or contact"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '300px' } }}
              size="sm"
            />
            <Button
              variant="outline"
              leftSection={<IconAdjustments size={16} />}
              onClick={() => setFiltersOpen(true)}
            >
              Filters
            </Button>
            {isFilterActive && (
              <Button variant="subtle" onClick={clearAllFilters}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <Group gap="xs">
            <Tooltip label="Refresh">
              <ActionIcon variant="subtle" onClick={() => fetchAll()} loading={loading} size="lg">
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/Information/CareSupportLeaders/Wizard/AddLeaderWizard')}>
              Add Leader
            </Button>
          </Group>
        </Flex>
      </Card>


      {/* ── Filter Drawer ── */}
      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filters"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <TextInput
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            radius="md"
          />
          <Select
            label="Leader Type"
            placeholder="Select a role"
            data={Array.from(new Set(leaders.map((l) => l.typeName || '')))
              .filter(Boolean)
              .map((r) => ({ value: r, label: r }))}
            value={selectedRole}
            onChange={setSelectedRole}
          />
          <Select
            label="Location"
            placeholder="Select a location"
            data={Array.from(
              new Set(leaders.flatMap((l) => l.locations?.map((loc) => loc.locationName ?? '') ?? []))
            )
              .filter(Boolean)
              .map((loc) => ({ value: loc, label: loc }))}
            value={selectedLocation}
            onChange={setSelectedLocation}
          />
          <Group align="right" mt="sm">
            <Button
              variant="light"
              onClick={() => {
                clearAllFilters();
                setFiltersOpen(false);
              }}
            >
              Clear
            </Button>
            <Button onClick={() => setFiltersOpen(false)}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Streaming progress ── */}
      <StreamProgress
        loading={loading}
        progress={progress}
        loadedCount={loadedCount}
        label="leaders"
      />

      {/* ── View toggle ── */}
      <Flex gap="sm" justify="flex-end" mb="md">
        <Button
          variant={view === 'cards' ? 'filled' : 'light'}
          color="blue"
          onClick={() => setView('cards')}
          aria-label="Cards view"
        >
          <IconLayoutGrid size={16} />
        </Button>

        <Button
          variant={view === 'table' ? 'filled' : 'light'}
          color="blue"
          onClick={() => setView('table')}
          aria-label="Table view"
        >
          <IconTable size={16} />
        </Button>
      </Flex>

      {/* ── Empty states (shared across both views) ── */}
      {hasNoLeadersAtAll ? (
        <Card withBorder radius="md" padding="lg">
          <EmptyState
            variant="no-data"
            entityLabel="care support leaders"
            addLabel="Add Leader"
            onAdd={() => navigate('/Information/CareSupportLeaders/Wizard/AddLeaderWizard')}
          />
        </Card>
      ) : hasNoFilteredResults ? (
        <Card withBorder radius="md" padding="lg">
          <EmptyState
            variant="no-results"
            entityLabel="leaders"
            onClearFilters={clearAllFilters}
          />
        </Card>
      ) : view === 'table' ? (
        /* ── Table view ── */
        <Card withBorder radius="md" padding="lg">
          <CCMSDataTable
            data={paginatedLeaders}
            columns={columns}
            loading={loading}
            sortable
            onSort={(config) => {
              setSortConfig(config);
              setActivePage(1);
            }}
            onRowClick={(leader) => goToLeader(leader)}
            striped
            highlightOnHover
          />
          <ListPagination
            page={activePage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={(page) => setActivePage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setActivePage(1);
            }}
            showPageSize
          />
        </Card>
       ) : (
          /* ── Card view ── */
          <Card withBorder radius="md" padding="lg">
            <SimpleGrid cols={4} spacing="lg" mb="md">
              {loading
                ? Array.from({ length: cardSize }).map((_, index) => (
                    <Box key={`leader-skeleton-${index}`}>
                      <Skeleton height={240} radius="md" />
                    </Box>
                  ))
                : paginatedLeaders.map((leader) => {
                    const contact = leaderToContact(leader);
                    const leaderId = contact.id;

                    return (
                      <Box key={leaderId} style={{ position: 'relative' }}>
                        <ContactCard
                          contact={contact}
                          showActiveCases
                          showNeedCount
                          onClick={() => goToLeader(leader)}
                        />
                      </Box>
                    );
                  })}
            </SimpleGrid>
            <ListPagination
              layout="grid"
              page={activePage}
              pageSize={cardSize}
              totalItems={totalItems}
              onPageChange={(page) => setActivePage(page)}
              onPageSizeChange={(size) => { setCardSize(size); setActivePage(1); }}
              showPageSize
            />
          </Card>
        )
      }
    </Container>
  );
}